import { Avatar, Chip, Stack, Typography } from '@mui/material';

import type { JiraSubtask } from '../../hooks/useJiraApi';

const PERSON_ROLES = [
    { key: 'playDesigner', label: 'Play Designer', color: '#7b61ff' },
    { key: 'artist', label: 'Artist', color: '#e8618c' },
    { key: 'techDesigner', label: 'Tech Designer', color: '#0d7b7c' },
    { key: 'qa', label: 'QA', color: '#16cd28' },
] as const;

type SubtaskAssigneeData = Pick<
    JiraSubtask,
    'playDesigner' | 'artist' | 'techDesigner' | 'qa'
>;

export interface SubtaskAssigneeChipsProps {
    subtask: SubtaskAssigneeData;
}

/** Up to two uppercase initials for an assignee avatar fallback. */
const initials = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');

/** Assignee chips for the play designer, artist, and technical designer. */
export const SubtaskAssigneeChips = ({
    subtask,
}: SubtaskAssigneeChipsProps) => {
    return (
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
            {PERSON_ROLES.map(({ key, label, color }) => {
                const user = subtask[key];

                return (
                    <Stack
                        key={key}
                        direction="row"
                        spacing={0.75}
                        sx={{ alignItems: 'center' }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 88 }}
                        >
                            {label}
                        </Typography>
                        <Chip
                            label={user?.name ?? 'Unassigned'}
                            size="small"
                            avatar={
                                user ? (
                                    user.avatar ? (
                                        <Avatar
                                            src={user.avatar}
                                            alt={user.name}
                                        />
                                    ) : (
                                        <Avatar
                                            sx={{
                                                bgcolor: color,
                                                color: 'white',
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {initials(user.name)}
                                        </Avatar>
                                    )
                                ) : undefined
                            }
                            sx={{ fontWeight: 500 }}
                        />
                    </Stack>
                );
            })}
        </Stack>
    );
};
